/**
 * The Job picker on a custom safety form (api#1103).
 *
 * Source: GET /safety/form-jobs — open jobs plus jobs finished in the last 14
 * tenant-local days. The API scopes it by role (office roles see the company,
 * everyone else only jobs they are crewed on), searches it with `q`, and
 * orders it; this component does none of those three things itself, so the
 * list a hand sees is the list the API decided.
 *
 * Why it exists: a template with `requires_job_link` refused every submit
 * from this screen, because the screen never sent a `job_id` and had no way
 * to choose one. Drafts no longer need a job (api#1103), so the picker gates
 * Submit only.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Check, Loader2, Search, X } from 'lucide-react';
import { api } from '../../api/client';
import LoadFailureNotice from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { formatCalendarDate, formatInstantTenantDay } from '../../lib/calendar-date';
import { jobStatusLabel } from '../../lib/job-status';
import { pickStr, pickerTitle } from '../../lib/job-picker';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';

/** One row of GET /safety/form-jobs. snake_case, like the rest of /safety submissions. */
export interface FormJobOption {
  id: string;
  job_number: string | null;
  status: string | null;
  /** Job wall-clock column stamped UTC verbatim — read its day as written. */
  scheduled_date: string | null;
  /** A real instant — read its day in the tenant's zone. */
  finished_at: string | null;
  customer_name: string | null;
  well_name: string | null;
  well_id_lsd: string | null;
  location_city: string | null;
  location_province_state: string | null;
  rig_name: string | null;
}

/** `J-0075 · Active · Oct 9`, or `… · Completed · finished Oct 12`. Absent parts read `—`. */
export function formJobMeta(job: FormJobOption, timeZone: string | null): string {
  const number = pickStr(job, 'jobNumber', 'job_number') ?? '—';
  const status = jobStatusLabel(job.status);
  const day = job.status === 'completed'
    ? `finished ${formatInstantTenantDay(job.finished_at, timeZone, { month: 'short', day: 'numeric' }) ?? '—'}`
    : formatCalendarDate(job.scheduled_date, { month: 'short', day: 'numeric' }) ?? '—';
  return `${number} · ${status} · ${day}`;
}

/** Well, then LSD, then city — the line that tells two jobs on one rig apart. */
function formJobWhere(job: FormJobOption): string | null {
  const well = pickStr(job, 'wellName', 'well_name');
  const lsd = pickStr(job, 'wellIdLsd', 'well_id_lsd');
  if (well && lsd) return `${well} · ${lsd}`;
  if (well || lsd) return well ?? lsd;
  const city = pickStr(job, 'locationCity', 'location_city');
  const prov = pickStr(job, 'locationProvinceState', 'location_province_state');
  return city ? [city, prov].filter(Boolean).join(', ') : null;
}

export const FORM_JOBS_KEY = ['safety', 'form-jobs'] as const;

function JobRowText({ job, timeZone }: { job: FormJobOption; timeZone: string | null }) {
  const title = pickerTitle(job);
  const where = formJobWhere(job);
  return (
    <span className="min-w-0 flex-1 text-left">
      <span className="flex min-w-0 items-baseline gap-1 text-sm font-medium text-white">
        <span className="truncate">{title.lead}</span>
        {title.rig && <span className="shrink-0">· {title.rig}</span>}
      </span>
      <span className="block truncate text-xs text-fieldiq-muted">{formJobMeta(job, timeZone)}</span>
      {where && <span className="block truncate text-xs text-fieldiq-muted">{where}</span>}
    </span>
  );
}

interface Props {
  value: FormJobOption | null;
  onChange: (job: FormJobOption | null) => void;
  required: boolean;
  error?: string;
}

export default function FormJobPicker({ value, onChange, required, error }: Props) {
  const timeZone = useTenantTimeZone();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(value === null);

  // The API does the search; debounce so a hand typing "vermilion" sends one
  // request, not nine.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const query = useQuery({
    queryKey: [...FORM_JOBS_KEY, q],
    queryFn: () =>
      api.safety.formJobs(q ? { q } : undefined).then(r => {
        const body = r.data;
        const rows = body && typeof body === 'object' && 'data' in body ? body.data : body;
        return (Array.isArray(rows) ? rows : []) as FormJobOption[];
      }),
    enabled: open,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const jobs = query.data;

  const pick = (job: FormJobOption) => {
    onChange(job);
    setOpen(false);
    setSearch('');
  };

  return (
    <div data-testid="form-job-picker" className="space-y-1">
      <p id="form-job-label" className="mb-1 block text-sm text-fieldiq-text">
        Job
        {required
          ? <span className="ml-1 text-red-400">*</span>
          : <span className="ml-1 text-xs text-fieldiq-muted">(optional)</span>}
      </p>

      {value && !open ? (
        <div className={`flex items-center gap-3 rounded-lg border bg-fieldiq-card px-3 py-2.5 ${error ? 'border-red-500' : 'border-fieldiq-border'}`}>
          <Briefcase aria-hidden="true" size={18} className="shrink-0 text-fieldiq-accent" />
          <JobRowText job={value} timeZone={timeZone} />
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 rounded px-2 py-1 text-sm font-medium text-fieldiq-accent hover:underline"
          >
            Change
          </button>
        </div>
      ) : (
        <div className={`rounded-lg border bg-fieldiq-card ${error ? 'border-red-500' : 'border-fieldiq-border'}`}>
          <div className="flex items-center gap-2 border-b border-fieldiq-border px-3 py-2">
            <Search aria-hidden="true" size={16} className="shrink-0 text-fieldiq-muted" />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search job #, customer, well, LSD, rig"
              aria-label="Search jobs"
              className="w-full bg-transparent py-1 text-sm text-white focus:outline-none"
            />
            {value && (
              <button
                type="button"
                onClick={() => { setOpen(false); setSearch(''); }}
                aria-label="Keep the selected job"
                className="shrink-0 rounded p-1 text-fieldiq-muted hover:text-white"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {failure ? (
            <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="jobs" className="py-6" />
          ) : jobs === undefined ? (
            <div className="flex justify-center py-6">
              <Loader2 aria-label="Loading jobs" size={20} className="animate-spin text-fieldiq-accent" />
            </div>
          ) : jobs.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-fieldiq-muted">
              {q
                ? `No jobs match "${q}".`
                : 'No open jobs, or jobs finished in the last 14 days, are available to you.'}
            </p>
          ) : (
            <ul aria-labelledby="form-job-label" className="max-h-72 divide-y divide-fieldiq-border overflow-y-auto">
              {jobs.map(job => {
                const selected = value?.id === job.id;
                return (
                  <li key={job.id}>
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => pick(job)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 focus-visible:outline-none"
                    >
                      <JobRowText job={job} timeZone={timeZone} />
                      {selected && <Check aria-hidden="true" size={16} className="shrink-0 text-fieldiq-accent" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {value && !required && !open && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-fieldiq-muted hover:underline"
        >
          Remove job
        </button>
      )}
      {error && <p className="mt-0.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}
